import type { MedplumClient } from '@medplum/core';
import type { Bundle, ExtractResource, ResourceType } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMedplum } from '@eluve/medplum-client';

import { type FhirReadError, classifyReadError } from './read-error';
import type { FhirPageControls } from './useFhirData';

/**
 * REST-side counterparts to useFhirData/useFhirConnection: the same
 * query-state shape for Medplum SDK operations that have no GraphQL surface
 * (`_history`, `Patient/$summary`, typeahead searches). One state machine —
 * client gating, cancellation, refetch — instead of a hand-rolled
 * useEffect + cancelled flag per hook.
 */
export type FhirRestReadState<T> = {
  /** Result of the read; null until loaded (or while skipped). */
  data: T | null;
  loading: boolean;
  error: Error | null;
  /** `error`, classified (403 OperationOutcome → forbidden) for toSectionStatus. */
  readError: FhirReadError | null;
  refetch: () => void;
  /** False while the Medplum client hasn't initialized (feature off, session pending). */
  enabled: boolean;
};

/**
 * Run a Medplum SDK read with shared query-state semantics.
 *
 * - `read` is invoked with the authenticated client; pass `null` to skip
 *   (missing id, query too short) — state stays empty, `loading` false.
 * - `deps` are the values `read` closes over — the read re-runs when they
 *   change, exactly like a useEffect dependency array.
 * - Stale responses are dropped (cancelled flag), never raced into state.
 *
 * @example
 * const { data: bundle } = useFhirRestRead(
 *   fhirPatientId
 *     ? (client) => client.get(client.fhirUrl('Patient', fhirPatientId, '$summary'), { cache: 'no-cache' })
 *     : null,
 *   [fhirPatientId],
 * );
 */
export function useFhirRestRead<T>(
  read: ((client: MedplumClient) => Promise<T>) | null,
  deps: readonly unknown[],
): FhirRestReadState<T> {
  const { client } = useMedplum();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [epoch, setEpoch] = useState(0);

  const skipped = !client || !read;
  useEffect(() => {
    if (skipped) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    read(client)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setData(null);
        setError(caught instanceof Error ? caught : new Error(String(caught)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `read` is a fresh closure every render — `deps` are its actual inputs.
  }, [client, skipped, epoch, ...deps]);

  return useMemo(
    () => ({
      data,
      loading,
      error,
      readError: error ? classifyReadError(error) : null,
      refetch: () => setEpoch((current) => current + 1),
      enabled: Boolean(client),
    }),
    [data, loading, error, client],
  );
}

const EMPTY_RESOURCES: never[] = [];

/**
 * Typeahead-friendly resource search over the SDK. Pass `null` params to
 * skip (query too short, dialog closed) — results clear without a request.
 * No debounce: the Medplum client dedupes/caches identical searches, which
 * is acceptable at dialog-typeahead scale.
 *
 * @example
 * const { resources: patients } = useFhirSearch(
 *   'Patient',
 *   query.trim().length >= 2 ? { name: query.trim(), _count: '8', _sort: 'family' } : null,
 * );
 */
export function useFhirSearch<K extends ResourceType>(
  resourceType: K,
  params: Record<string, string> | null,
): {
  resources: ExtractResource<K>[];
  loading: boolean;
  error: Error | null;
  readError: FhirReadError | null;
  enabled: boolean;
} {
  // Key the effect on the serialized params, not object identity — callers
  // pass a fresh object literal every render.
  const paramsKey = params ? JSON.stringify(params) : null;
  const { data, loading, error, readError, enabled } = useFhirRestRead(
    params
      ? async (client) => [
          ...(await client.searchResources(resourceType, params)),
        ]
      : null,
    [resourceType, paramsKey],
  );
  return {
    resources: data ?? EMPTY_RESOURCES,
    loading,
    error,
    readError,
    enabled,
  };
}

type SearchPage<K extends ResourceType> = {
  resources: ExtractResource<K>[];
  /** `link.next`, absolute; null on the last page. */
  nextUrl: string | null;
  total: number | null;
};

function readSearchPage<K extends ResourceType>(
  bundle: Bundle,
): SearchPage<K> {
  return {
    resources: (bundle.entry ?? [])
      .map((entry) => entry.resource)
      .filter((resource): resource is ExtractResource<K> =>
        Boolean(resource),
      ),
    nextUrl: bundle.link?.find((link) => link.relation === 'next')?.url ?? null,
    total: bundle.total ?? null,
  };
}

/**
 * REST search with the same bounded paging contract as useFhirConnection: one
 * page up front, one more per explicit loadMore() (LoadMoreRow is the
 * affordance), never a fetch-all loop. Use it for lists the user reads to the
 * end — `useFhirSearch` returns page one only, so it silently truncates at
 * `_count`, which reads as missing data rather than fewer results.
 *
 * `_count` on `params` sets the page size; `_total: 'accurate'` is requested
 * so the affordance can show loaded-of-total.
 */
export function useFhirSearchPaged<K extends ResourceType>(
  resourceType: K,
  params: Record<string, string> | null,
): FhirPageControls & {
  resources: ExtractResource<K>[];
  loading: boolean;
  error: Error | null;
  readError: FhirReadError | null;
  enabled: boolean;
} {
  const { client } = useMedplum();
  const paramsKey = params ? JSON.stringify(params) : null;

  const first = useFhirRestRead<SearchPage<K>>(
    params
      ? async (searchClient) =>
          readSearchPage<K>(
            await searchClient.search(resourceType, {
              _total: 'accurate',
              ...params,
            }),
          )
      : null,
    [resourceType, paramsKey],
  );

  // Pages appended by loadMore, tagged with the first-page result they extend
  // so a refetch/param change drops them in the same render rather than one
  // render later (which would show the new page one with the old tail).
  const [appended, setAppended] = useState<{
    base: SearchPage<K> | null;
    state: SearchPage<K>;
  } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const current =
    appended && appended.base === first.data ? appended.state : first.data;

  const loadMore = useCallback(() => {
    const nextUrl = current?.nextUrl;
    if (!client || !nextUrl || loadingMore) return;
    const base = first.data;
    const from = current;
    setLoadingMore(true);
    client
      .get<Bundle>(nextUrl)
      .then((bundle) => {
        const page = readSearchPage<K>(bundle);
        setAppended({
          base,
          state: {
            resources: [...from.resources, ...page.resources],
            nextUrl: page.nextUrl,
            total: from.total ?? page.total,
          },
        });
      })
      // Failure leaves the loaded pages intact; the button stays for a retry.
      .catch(() => undefined)
      .finally(() => setLoadingMore(false));
  }, [client, current, first.data, loadingMore]);

  const resources = current?.resources ?? EMPTY_RESOURCES;
  return {
    resources,
    loading: first.loading,
    error: first.error,
    readError: first.readError,
    enabled: first.enabled,
    loaded: resources.length,
    count: current?.total ?? null,
    hasMore: Boolean(current?.nextUrl),
    loadMore,
    loadingMore,
  };
}
